import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  ArchiveBoxIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PlusIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import { fetchDocumentFormsArchive } from "../../../../../api/documentForms";
import NewDocumentFormDialog from "../../../../../components/dashboard/document-forms/NewDocumentFormDialog";

const ARCHIVE_BASE = "/dashboard/hse-ks/documents/form-archive";

function statusBadge(status) {
  return status === "final"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

export const DocumentFormsArchive = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const templateFilter = searchParams.get("template_id") || "";
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ total_forms: 0, template_count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const createMatch = useMatch(`${ARCHIVE_BASE}/new`);
  const [createOpen, setCreateOpen] = useState(Boolean(createMatch));
  const [createTemplateId, setCreateTemplateId] = useState(templateFilter || "");

  useEffect(() => {
    if (createMatch) setCreateOpen(true);
  }, [createMatch]);

  const load = useCallback(async (q, templateId) => {
    setIsLoading(true);
    const res = await fetchDocumentFormsArchive({
      query: q,
      document_form_template_id: templateId || undefined,
      per_page: 500,
    });
    const data = res?.data || [];
    setGroups(data);
    setMeta(res?.meta || { total_forms: 0, template_count: data.length });
    setExpanded(
      data.reduce((acc, group) => {
        acc[group.template?.id] = true;
        return acc;
      }, {})
    );
    setIsLoading(false);
  }, []);

  const debouncedLoad = useMemo(() => debounce((q, tid) => load(q, tid), 250), [load]);

  useEffect(() => {
    debouncedLoad(query, templateFilter);
    return () => debouncedLoad.cancel();
  }, [query, templateFilter, debouncedLoad]);

  const toggleGroup = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <HsePageChrome
      title={t("documentForms.archive.title")}
      subtitle={t("documentForms.archive.subtitle")}
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/dashboard/hse-ks/documents/templates"
              className="font-semibold text-slate-600 hover:text-slate-900"
            >
              ← {t("documentForms.templates.title")}
            </Link>
            {templateFilter ? (
              <Link
                to={ARCHIVE_BASE}
                className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700"
              >
                {t("documentForms.archive.clear_filter")}
              </Link>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              setCreateTemplateId(templateFilter || "");
              setCreateOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
          >
            <PlusIcon className="h-4 w-4" />
            {t("documentForms.new")}
          </button>
        </div>

        <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <ArchiveBoxIcon className="h-5 w-5 text-slate-400" />
              <span>
                {t("documentForms.archive.stats", {
                  forms: meta.total_forms || 0,
                  templates: meta.template_count || 0,
                })}
              </span>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("documentForms.search_placeholder")}
              className="w-full max-w-md rounded-full border border-slate-200 px-4 py-2 text-sm sm:w-auto"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-12 text-sm text-slate-500 shadow-sm">
            {t("Loading")}...
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-12 text-sm text-slate-500 shadow-sm">
            {t("documentForms.archive.empty")}
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => {
              const template = group.template || {};
              const isOpen = expanded[template.id] !== false;
              return (
                <section
                  key={template.id || "unknown"}
                  className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm"
                >
                  <div className="flex w-full items-center justify-between gap-4 px-6 py-5 hover:bg-slate-50">
                    <button
                      type="button"
                      onClick={() => toggleGroup(template.id)}
                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    >
                      {isOpen ? (
                        <ChevronDownIcon className="mt-1 h-5 w-5 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-slate-400" />
                      )}
                      <div className="min-w-0">
                        <div className="text-lg font-semibold text-slate-950">
                          {template.title || t("documentForms.archive.unknown_template")}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
                            {t("documentForms.archive.count", { count: group.count || 0 })}
                          </span>
                          {template.hse_document?.id ? (
                            <Link
                              to={`/dashboard/hse-ks/documents/${template.hse_document.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-semibold text-blue-700 hover:text-blue-900"
                            >
                              {t("documentForms.archive.hse_link")}
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCreateTemplateId(String(template.id));
                        setCreateOpen(true);
                      }}
                      className="shrink-0 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
                    >
                      + {t("documentForms.new")}
                    </button>
                  </div>

                  {isOpen ? (
                    <ul className="divide-y divide-slate-100 border-t border-slate-100">
                      {(group.forms || []).map((doc) => (
                        <li key={doc.id} className="flex items-center justify-between px-6 py-4 pl-14">
                          <div className="flex items-start gap-3">
                            <DocumentTextIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                {doc.display_reference ? (
                                  <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-bold text-white">
                                    {doc.display_reference}
                                  </span>
                                ) : null}
                                <span className="font-medium text-slate-900">{doc.title}</span>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                <span className={`rounded-full px-2 py-0.5 font-semibold ${statusBadge(doc.status)}`}>
                                  {doc.status === "final"
                                    ? t("documentForms.status.final")
                                    : t("documentForms.status.draft")}
                                </span>
                                <span>{formatDate(doc.updated_at)}</span>
                                {doc.subject_label ? <span>{doc.subject_label}</span> : null}
                                {doc.author?.displayTitle ? <span>{doc.author.displayTitle}</span> : null}
                              </div>
                            </div>
                          </div>
                          <Link
                            to={`${ARCHIVE_BASE}/${doc.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            {t("Open")}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <NewDocumentFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultTemplateId={createTemplateId}
        onCreated={() => load(query, templateFilter)}
      />
    </HsePageChrome>
  );
};

// Backward-compatible export name
export const DocumentFormsList = DocumentFormsArchive;
